<template>
  <div class="offers-page">
    <header class="page-heading">
      <div>
        <p>Acquisitions</p>
        <h1>Annonces suivies</h1>
        <span>Surveillez les échéances de vos enchères en cours.</span>
      </div>
    </header>

    <section class="summary-strip" aria-label="Résumé des annonces">
      <div>
        <strong>{{ totalOffers }}</strong
        ><span>Annonces au total</span>
      </div>
      <div>
        <strong>{{ activeOfferCount }}</strong
        ><span>En cours dans cette vue</span>
      </div>
      <div class="summary-strip__hint">
        <span class="live-dot"></span
        ><span>{{
          nearestFirst
            ? "Les fins les plus proches apparaissent en premier"
            : "Les échéances les plus récentes apparaissent en premier"
        }}</span>
      </div>
    </section>

    <section class="offers-panel">
      <div class="status-tabs" role="tablist" aria-label="Filtrer par statut">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          type="button"
          role="tab"
          :aria-selected="filters.status === tab.value"
          :class="{ active: filters.status === tab.value }"
          @click="setStatusFilter(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="toolbar">
        <label class="search-control">
          <i class="fas fa-search" aria-hidden="true"></i>
          <span class="sr-only">Rechercher une annonce</span>
          <input
            v-model="filters.contains"
            type="search"
            placeholder="Rechercher dans toutes les annonces…"
            @input="debouncedFilter"
          />
        </label>
        <select
          v-model="filters.source"
          class="source-filter"
          aria-label="Filtrer par source"
          @change="filterOffers"
        >
          <option value="">Toutes les sources</option>
          <option v-for="(source, id) in SOURCES" :key="id" :value="id">
            {{ source.longLabel }}
          </option>
        </select>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="reset-action"
          @click="resetFilters"
        >
          Effacer les filtres
        </button>
      </div>
      <p v-if="searchingAll" class="search-scope">
        Recherche dans toutes vos annonces suivies (en cours, remportées et
        terminées).
      </p>

      <main ref="mainContentOffers" class="offers-content">
        <div v-if="loading" class="loader-container">
          <LoaderDiv
            :isLoading="true"
            variant="modern"
            size="medium"
            loaderText="Chargement des annonces…"
          />
        </div>

        <div
          v-else-if="
            visibleOffers.length === 0 && !loadingMore && hasInitialLoad
          "
          class="empty-state"
        >
          <span class="empty-state__icon"
            ><i class="fas fa-gavel" aria-hidden="true"></i
          ></span>
          <h2>
            {{
              hasActiveFilters
                ? "Aucune annonce ne correspond"
                : "Aucune annonce en cours"
            }}
          </h2>
          <p>
            {{
              hasActiveFilters
                ? "Modifiez ou effacez les filtres pour élargir les résultats."
                : "Les annonces terminées ou remportées restent dans les onglets Remportées, Terminées et Toutes."
            }}
          </p>
          <button
            v-if="hasActiveFilters"
            type="button"
            class="secondary-action"
            @click="resetFilters"
          >
            Réinitialiser les filtres
          </button>
          <router-link v-else to="/parse" class="primary-action"
            >Suivre une annonce</router-link
          >
        </div>

        <div v-else class="table-scroll">
          <table class="offers-table">
            <thead>
              <tr>
                <th>Annonce</th>
                <th>Source</th>
                <th>Échéance</th>
                <th>Statut</th>
                <th><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="offer in visibleOffers"
                :key="offer._key"
                :class="getTimeClass(offer.endtime)"
              >
                <td>
                  <div class="identity-cell">
                    <button
                      type="button"
                      class="offer-identity"
                      @click="showDetails(offer)"
                    >
                      <span class="thumbnail-wrap"
                        ><img
                          :src="
                            offer.thumbnail_url ||
                            `${cdnUrl}/images/${offer._key}/${offer._key}_thumbnail.jpg`
                          "
                          :alt="offer.title || 'Miniature de l’annonce'"
                          loading="lazy"
                          @error="handleImageError"
                      /></span>
                      <span class="identity-copy"
                        ><strong>{{
                          offer.title || "Annonce sans titre"
                        }}</strong
                        ><small
                          >#{{ offer._key
                          }}<span class="compact-source">
                            · {{ getSourceInfo(offer.source).label }}</span
                          ></small
                        ><small v-if="offer.cote" class="cote-line"
                          ><CoteChip :cote="offer.cote" :price="offer.price"
                        /></small>
                      </span>
                    </button>
                    <button
                      v-if="
                        !offer.owns && getTimeClass(offer.endtime) === 'ended'
                      "
                      type="button"
                      class="won-badge"
                      title="Marquer comme remportée"
                      aria-label="Marquer comme remportée"
                      :disabled="ownershipLoading === offer._key"
                      @click.stop="toggleOwnership(offer)"
                    >
                      <i
                        :class="
                          ownershipLoading === offer._key
                            ? 'fas fa-spinner fa-spin'
                            : 'fas fa-trophy'
                        "
                      ></i>
                    </button>
                  </div>
                </td>
                <td>
                  <span class="source-tag">{{
                    getSourceInfo(offer.source).label
                  }}</span>
                </td>
                <td>
                  <div class="deadline">
                    <strong>{{ deadlineLabel(offer) }}</strong
                    ><small>{{
                      offer.endtime
                        ? formatDate(offer.endtime)
                        : "Prix fixe, sans date de fin"
                    }}</small>
                  </div>
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="getHistoryStatus(offer).class"
                    ><span></span>{{ getHistoryStatus(offer).label }}</span
                  >
                </td>
                <td>
                  <div class="row-actions">
                    <button
                      type="button"
                      class="refresh-button"
                      :disabled="isRefreshing(offer._key)"
                      @click.stop="refreshFromList(offer)"
                    >
                      <span
                        :class="{ spinning: isRefreshing(offer._key) }"
                        aria-hidden="true"
                        >↻</span
                      >
                      <span class="refresh-label">{{
                        isRefreshing(offer._key)
                          ? "Actualisation…"
                          : "Actualiser"
                      }}</span>
                    </button>
                    <button
                      type="button"
                      class="more-button"
                      @click="showDetails(offer)"
                    >
                      Détails <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="loadingMore" class="loading-more">
          <span class="spinner-circle"></span> Chargement…
        </div>
        <p v-if="allOffersLoaded && offers.length" class="end-indicator">
          {{ offers.length }} annonce{{
            offers.length > 1 ? "s" : ""
          }}
          affichée{{ offers.length > 1 ? "s" : "" }}
        </p>
      </main>
    </section>

    <OfferQuickPanel
      :visible="isModalVisible"
      :offer="selectedOffer"
      :loading="loadingPopup"
      show-ownership
      :ownership-loading="ownershipLoading === selectedOffer._key"
      @close="closeModal"
      @refresh="refreshOffer"
      @toggle-ownership="toggleOwnership"
      @price-updated="(fields) => Object.assign(selectedOffer, fields)"
      @open-acquisition="openAcquisition"
      @open-package="openPackage"
      @unfollow="unfollowOffer"
    />
    <OfferCreatePopup
      :isVisible="isCreateModalVisible"
      @close="closeCreateModal"
      @created="onOfferCreated"
    />
    <Transition name="offers-toast"
      ><div v-if="showToast" class="offers-toast" :class="toastType">
        <div class="offers-toast__content">
          <i :class="toastIcon" aria-hidden="true"></i
          ><span>{{ toastMessage }}</span>
        </div>
        <button
          class="offers-toast__close"
          aria-label="Fermer la notification"
          @click="hideToast"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button></div
    ></Transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config.js";
import { parseAndWait } from "@/api/parseJobs.js";
import { isTokenExpired } from "../../auth";
import LoaderDiv from "../LoaderDiv.vue";
import { dropQueryParams } from "../../urlQuery";
import { navigateFromDetail, openDetail } from "../../detailStack";
import OfferQuickPanel from "./OfferQuickPanel.vue";
import CoteChip from "../common/CoteChip.vue";
import { SOURCES, sourceInfo } from "../../sources";
import {
  formatDate as formatDateText,
  formatRemaining,
  hoursLeft,
  parseTime,
  useNow,
} from "../../clock";
import OfferCreatePopup from "./OfferCreatePopup.vue";

export default {
  name: "OffersList",
  components: {
    CoteChip,
    LoaderDiv,
    OfferQuickPanel,
    OfferCreatePopup,
  },
  setup() {
    const router = useRouter();

    // Reactive state
    const offers = ref([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const loadingPopup = ref(false);
    const loadingIndex = ref(null);
    // Annonces en cours d'actualisation (plusieurs en parallèle, non bloquant)
    const refreshingKeys = ref(new Set());
    const isRefreshing = (key) => refreshingKeys.value.has(key);
    const setRefreshing = (key, active) => {
      const next = new Set(refreshingKeys.value);
      if (active) next.add(key);
      else next.delete(key);
      refreshingKeys.value = next;
    };
    const selectedOffer = ref({});
    const isModalVisible = ref(false);
    const currentPage = ref(0);
    const totalOffers = ref(0);
    const allOffersLoaded = ref(false);
    const hasInitialLoad = ref(false);
    const mainContentOffers = ref(null);
    const offersGrid = ref(null);
    const viewMode = ref("list");
    // Opens on what is still available: an ended auction or a sold item
    // leaves "En cours" on its own, and stays followed (Terminées, Toutes)
    const DEFAULT_STATUS = "active";
    const statusTabs = [
      { label: "En cours", value: "active" },
      { label: "Remportées", value: "won" },
      { label: "Terminées", value: "lost" },
      { label: "Toutes", value: "" },
    ];

    // Create modal
    const isCreateModalVisible = ref(false);

    // Filters
    const filters = ref({
      id: "",
      status: DEFAULT_STATUS,
      contains: "",
      source: "",
    });
    // A search typed from "En cours" looks in every followed offer: an
    // ended one is always found
    const searchingAll = computed(
      () =>
        filters.value.status === DEFAULT_STATUS &&
        Boolean(filters.value.contains.trim() || filters.value.id),
    );

    // Toast notifications
    const showToast = ref(false);
    const toastMessage = ref("");
    const toastType = ref("info");

    // Constants
    const limit = config.itemsLimit;
    const cdnUrl = config.cdnUrl;

    // Computed properties
    const activeOfferCount = computed(
      () =>
        offers.value.filter(
          (offer) => getHistoryStatus(offer).class === "active",
        ).length,
    );
    // "En cours": the nearest end first, items without an end date after
    // (as the API pages them); elsewhere the most recent first
    const nearestFirst = computed(
      () => filters.value.status === DEFAULT_STATUS && !searchingAll.value,
    );
    const visibleOffers = computed(() => {
      if (nearestFirst.value)
        return [...offers.value].sort((first, second) => {
          const firstEnd = parseTime(first.endtime);
          const secondEnd = parseTime(second.endtime);
          if (Number.isNaN(firstEnd) || Number.isNaN(secondEnd))
            return Number.isNaN(firstEnd) - Number.isNaN(secondEnd);
          return firstEnd - secondEnd;
        });
      return [...offers.value].sort((firstOffer, secondOffer) => {
        const firstEndtime = parseTime(firstOffer.endtime);
        const secondEndtime = parseTime(secondOffer.endtime);
        const safeFirstEndtime = Number.isNaN(firstEndtime)
          ? Number.NEGATIVE_INFINITY
          : firstEndtime;
        const safeSecondEndtime = Number.isNaN(secondEndtime)
          ? Number.NEGATIVE_INFINITY
          : secondEndtime;
        return safeSecondEndtime - safeFirstEndtime;
      });
    });

    const hasActiveFilters = computed(
      () =>
        filters.value.id ||
        filters.value.status !== DEFAULT_STATUS ||
        filters.value.contains ||
        filters.value.source,
    );

    const toastIcon = computed(() => {
      switch (toastType.value) {
        case "success":
          return "fas fa-check-circle";
        case "error":
          return "fas fa-exclamation-circle";
        case "warning":
          return "fas fa-exclamation-triangle";
        default:
          return "fas fa-info-circle";
      }
    });

    // Debounce function
    let debounceTimer = null;
    const debouncedFilter = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        filterOffers();
      }, 300);
    };

    // Methods
    const showToastMessage = (message, type = "info") => {
      toastMessage.value = message;
      toastType.value = type;
      showToast.value = true;

      setTimeout(() => {
        hideToast();
      }, 5000);
    };

    const hideToast = () => {
      showToast.value = false;
    };

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        showToastMessage(`ID ${text} copié dans le presse-papier`, "success");
      } catch (error) {
        console.warn("Clipboard API not available, falling back to selection");
        showToastMessage("ID copié", "success");
      }
    };

    const handleImageError = (event) => {
      event.target.style.display = "none";
    };

    const formatDate = (dateString) =>
      formatDateText(
        dateString,
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        },
        "Date inconnue",
      );

    // The clock ticks every second: remaining times count down on their own
    const now = useNow(1000);
    const getTimeClass = (endtime) => {
      const left = hoursLeft(endtime, now.value);
      if (Number.isNaN(left)) return "";
      if (left < 0) return "ended";
      if (left < 1) return "ending-soon";
      if (left < 24) return "ending-today";
      return "";
    };

    const getHistoryStatus = (offer) => {
      const lifecycle =
        offer.lifecycle ||
        (offer.owns
          ? "won"
          : getTimeClass(offer.endtime) === "ended"
            ? "lost"
            : "active");

      if (lifecycle === "won") {
        return {
          label: "Remportée",
          class: "owned",
          icon: "fas fa-check-circle",
        };
      }
      if (lifecycle === "lost") {
        return {
          // Sites without an end date: sold or removed (checked every 6 h)
          label:
            {
              sold: `Vendue sur ${sourceInfo(offer.source).label}`,
              removed: "Annonce retirée",
            }[offer.availability] || "Terminée",
          class: "lost",
          icon: "fas fa-times-circle",
        };
      }
      return { label: "En cours", class: "active", icon: "fas fa-clock" };
    };

    const getOwnershipIcon = (offer) => {
      if (loadingIndex.value === offer._key) return "fas fa-spinner fa-spin";
      return offer.owns ? "fas fa-times" : "fas fa-check";
    };

    const toggleViewMode = (mode) => {
      viewMode.value = mode;
    };

    const setStatusFilter = (status) => {
      filters.value.status = status;
      filterOffers();
    };

    // Sites without an end date (Mercari, Vinted): their state instead
    const deadlineLabel = (offer) => {
      if (offer.endtime) return formatRelativeEnd(offer.endtime);
      if (offer.availability === "sold")
        return `Vendue sur ${sourceInfo(offer.source).label}`;
      if (offer.availability === "removed") return "Annonce retirée";
      return "En vente";
    };

    const formatRelativeEnd = (dateString) => {
      const remaining = formatRemaining(dateString, now.value);
      if (!remaining) return "Échéance inconnue";
      return remaining === "Terminée" ? remaining : `${remaining} restant`;
    };

    const resetFilters = () => {
      filters.value = {
        id: "",
        status: DEFAULT_STATUS,
        contains: "",
        source: "",
      };
      filterOffers();
    };

    const filterOffers = async () => {
      listRequest++; // the request in progress is for the previous filters
      currentPage.value = 0;
      allOffersLoaded.value = false;
      loadingMore.value = false;
      offers.value = [];
      selectedOffer.value = {};
      hasInitialLoad.value = false;
      loading.value = true;
      await loadOffers();
    };

    // Query of the list with the current filters
    const listParams = () => {
      const params = new URLSearchParams();
      if (filters.value.id) params.append("id", filters.value.id);
      if (filters.value.contains.trim())
        params.append("contains", filters.value.contains.trim());
      if (filters.value.source) params.append("source", filters.value.source);
      if (filters.value.status && !searchingAll.value)
        params.append("lifecycle", filters.value.status);
      return params;
    };

    // Every minute: the offers already on screen are read again (end time,
    // price, status, guide), page by page as loaded. Nothing is added,
    // removed nor moved: the scroll position, the filters and the loaded
    // pages stay as they are.
    const REFRESHED_FIELDS = [
      "endtime",
      "price",
      "owns",
      "lifecycle",
      "cote",
      "followed_notify",
      "availability",
    ];
    let refreshingLoaded = false;
    const refreshLoaded = async () => {
      if (
        refreshingLoaded ||
        loading.value ||
        loadingMore.value ||
        !offers.value.length
      )
        return;
      refreshingLoaded = true;
      try {
        const params = listParams().toString();
        for (let page = 0; page < currentPage.value; page += 1) {
          const { data } = await axios.get(
            `${config.apiUrl}/offers?page=${page}&limit=${limit}&${params}`,
          );
          for (const fresh of data?.result || []) {
            const offer = offers.value.find(
              (entry) => entry._key === fresh._key,
            );
            if (offer)
              REFRESHED_FIELDS.forEach((field) => {
                if (field in fresh) offer[field] = fresh[field];
              });
          }
        }
      } catch {
        // network hiccup: next minute
      } finally {
        refreshingLoaded = false;
      }
    };

    // Only the answer to the latest request counts (filters changed, scroll
    // during the first load): no duplicate, no page skipped
    let listRequest = 0;
    let listBusyFor = -1;
    const loadOffers = async () => {
      if (allOffersLoaded.value || listBusyFor === listRequest) {
        return;
      }

      const token = localStorage.getItem("access_token");
      if (isTokenExpired(token)) {
        return;
      }

      const request = ++listRequest;
      listBusyFor = request;
      try {
        if (currentPage.value === 0) {
          loading.value = true;
        } else {
          loadingMore.value = true;
        }

        const params = listParams();

        const response = await axios.get(
          `${config.apiUrl}/offers?page=${currentPage.value}&limit=${limit}&${params.toString()}`,
        );
        if (request !== listRequest) return;

        if (response.data && response.data.result) {
          offers.value.push(...response.data.result);
          totalOffers.value = response.data.total_items || 0;
          currentPage.value += 1;

          if (offers.value.length >= totalOffers.value) {
            allOffersLoaded.value = true;
          }
        }

        hasInitialLoad.value = true;
      } catch (error) {
        if (request !== listRequest) return;
        console.error("Erreur lors de la récupération des offres:", error);
        showToastMessage("Erreur lors du chargement des offres", "error");
      } finally {
        if (request === listRequest) {
          loading.value = false;
          loadingMore.value = false;
          hasInitialLoad.value = true;
          listBusyFor = -1;
        }
      }
    };

    const handleScroll = async () => {
      if (allOffersLoaded.value || loadingMore.value || loading.value) return;
      const scrollPosition = window.scrollY + window.innerHeight;
      const loadThreshold = document.documentElement.scrollHeight - 500;
      if (scrollPosition >= loadThreshold) {
        await loadOffers();
      }
    };

    // Two sheets opened quickly: the second one never shows the first
    let detailsRequest = 0;
    const showDetails = async (offer) => {
      const request = ++detailsRequest;
      selectedOffer.value = offer;
      isModalVisible.value = true;
      loadingPopup.value = true;

      try {
        const response = await axios.get(
          `${config.apiUrl}/offers/${offer._key}`,
        );
        if (request === detailsRequest) selectedOffer.value = response.data;
      } catch (error) {
        if (request !== detailsRequest) return;
        console.error("Erreur lors de la récupération des détails:", error);
        showToastMessage(
          "Le détail de l’annonce n’a pas pu être chargé",
          "error",
        );
      } finally {
        if (request === detailsRequest) loadingPopup.value = false;
      }
    };

    const closeModal = () => {
      isModalVisible.value = false;
    };

    const refreshOffer = async (offer) => {
      if (!offer.url) {
        showToastMessage("URL de l'offre non disponible", "warning");
        return;
      }
      if (isRefreshing(offer._key)) return;

      setRefreshing(offer._key, true);
      try {
        // L'analyse tourne en arrière-plan : la ligne affiche son indicateur
        // pendant ce temps, le reste de la liste reste utilisable
        const outcome = await parseAndWait(offer.url, true);
        if (outcome.status === "Failed") {
          showToastMessage(
            outcome.error || "L’actualisation de l’annonce a échoué",
            "error",
          );
          return;
        }
        if (outcome.status === "Timeout") {
          showToastMessage(
            "L’actualisation continue en arrière-plan",
            "warning",
          );
          return;
        }

        // Mettre à jour la ligne de la liste avec les nouvelles données
        const { data } = await axios.get(
          `${config.apiUrl}/offers/${offer._key}`,
        );
        const listEntry = offers.value.find(
          (entry) => entry._key === offer._key,
        );
        if (listEntry && data) {
          Object.assign(listEntry, {
            title: data.title,
            description: data.description,
            ...Object.fromEntries(
              REFRESHED_FIELDS.filter((field) => field in data).map(
                (field) => [field, data[field]],
              ),
            ),
          });
        }
        // The sheet of this offer, if open, shows the new data too
        if (isModalVisible.value && selectedOffer.value?._key === offer._key)
          selectedOffer.value = data;
        showToastMessage(
          `Annonce actualisée : ${data?.title || offer._key}`,
          "success",
        );
      } catch (error) {
        console.error("Erreur lors de l'actualisation:", error);
        showToastMessage("Erreur lors de l'actualisation", "error");
      } finally {
        setRefreshing(offer._key, false);
      }
    };

    const refreshFromList = (offer) => {
      isModalVisible.value = false;
      selectedOffer.value = {};
      return refreshOffer(offer);
    };

    const ownershipLoading = ref(null);

    const toggleOwnership = async (offer) => {
      if (!offer?._key || ownershipLoading.value) return;
      const newStatus = !offer.owns;

      // Removing the "owns" link also deletes the costs entered for this purchase
      if (
        !newStatus &&
        !window.confirm(
          "Retirer cette annonce de vos achats ? Les coûts et références saisis pour cet achat seront supprimés.",
        )
      )
        return;

      try {
        ownershipLoading.value = offer._key;
        if (newStatus) {
          await axios.post(`${config.apiUrl}/offers/${offer._key}/owns`, {});
        } else {
          await axios.delete(`${config.apiUrl}/offers/${offer._key}/owns`);
        }

        const ended = getTimeClass(offer.endtime) === "ended";
        const lifecycle = newStatus ? "won" : ended ? "lost" : "active";
        const listEntry = offers.value.find(
          (entry) => entry._key === offer._key,
        );
        for (const target of [listEntry, selectedOffer.value]) {
          if (target && target._key === offer._key) {
            target.owns = newStatus;
            target.lifecycle = lifecycle;
          }
        }

        showToastMessage(
          newStatus
            ? "Annonce marquée comme remportée. Elle apparaît dans vos achats."
            : "Annonce retirée de vos achats.",
          "success",
        );
      } catch (error) {
        console.error("Erreur lors de la mise à jour du statut:", error);
        showToastMessage(
          "Le statut de l’annonce n’a pas pu être mis à jour",
          "error",
        );
      } finally {
        ownershipLoading.value = null;
      }
    };

    const unfollowOffer = async (offer) => {
      if (!offer?._key) return;
      if (
        !window.confirm(
          "Ne plus suivre cette annonce ? Elle disparaîtra de la liste et ses rappels seront annulés.",
        )
      )
        return;

      try {
        await axios.delete(`${config.apiUrl}/offers/${offer._key}/follow`);
        offers.value = offers.value.filter(
          (entry) => entry._key !== offer._key,
        );
        totalOffers.value = Math.max(0, totalOffers.value - 1);
        isModalVisible.value = false;
        showToastMessage("Annonce retirée de vos annonces suivies", "success");
      } catch (error) {
        console.error("Erreur lors de l’arrêt du suivi:", error);
        showToastMessage("L’annonce n’a pas pu être retirée du suivi", "error");
      }
    };

    // The purchase opens over the offer (way back to it); the package editor
    // is a page: the offer is shown again when coming back
    const openAcquisition = (offer) =>
      openDetail("purchase", offer._key, {
        from: "offer",
        fromId: offer._key,
      });
    const openPackage = (packageId) =>
      navigateFromDetail(`/packages/${packageId}`, {
        pagePanel: isModalVisible.value
          ? { type: "offer", id: selectedOffer.value._key }
          : null,
      });

    const getSourceInfo = sourceInfo;

    const openCreateModal = () => {
      isCreateModalVisible.value = true;
    };

    const closeCreateModal = () => {
      isCreateModalVisible.value = false;
    };

    const onOfferCreated = () => {
      closeCreateModal();
      filterOffers();
      showToastMessage("Offre créée avec succès", "success");
    };

    // Lifecycle
    // A guide computed in a sheet ("Calculer la cote"…): the row is updated
    // An offer changed in a sheet (cote computed, won, no longer followed):
    // its row follows, the rest of the list stays as it is
    const onChanged = (event) => {
      const { type, record } = event.detail || {};
      if (type !== "offer" || !record?._key) return;
      if (record.followed === false) {
        offers.value = offers.value.filter((entry) => entry._key !== record._key);
        totalOffers.value = Math.max(0, totalOffers.value - 1);
        return;
      }
      const offer = offers.value.find((entry) => entry._key === record._key);
      if (!offer) return;
      ["cote", "owns", "price", "bids", "endtime"].forEach((field) => {
        if (field in record) offer[field] = record[field];
      });
    };
    window.addEventListener("prexens:changed", onChanged);

    onMounted(async () => {
      const token = localStorage.getItem("access_token");
      if (isTokenExpired(token)) {
        // Handle redirect outside of Vue instance
        return;
      }
      await loadOffers();
      window.addEventListener("scroll", handleScroll, { passive: true });

      // Lien depuis une notification : /offers?offer=<id> ouvre le détail
      const requested = String(router.currentRoute.value.query.offer || "");
      if (requested) {
        showDetails({ _key: requested });
        dropQueryParams("offer");
      }
    });

    const refreshTimer = setInterval(() => {
      if (document.visibilityState === "visible") refreshLoaded();
    }, 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshLoaded();
    };
    document.addEventListener("visibilitychange", onVisible);
    onUnmounted(() => {
      clearInterval(refreshTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("prexens:changed", onChanged);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      // Refs
      offers,
      activeOfferCount,
      visibleOffers,
      statusTabs,
      searchingAll,
      nearestFirst,
      deadlineLabel,
      loading,
      loadingMore,
      loadingPopup,
      loadingIndex,
      isRefreshing,
      selectedOffer,
      isModalVisible,
      totalOffers,
      allOffersLoaded,
      hasInitialLoad,
      filters,
      viewMode,
      mainContentOffers,
      offersGrid,
      showToast,
      toastMessage,
      toastType,

      // Constants
      cdnUrl,

      // Computed
      hasActiveFilters,
      toastIcon,

      // Create modal
      isCreateModalVisible,
      openCreateModal,
      closeCreateModal,
      onOfferCreated,
      getSourceInfo,
      SOURCES,

      // Methods
      debouncedFilter,
      showToastMessage,
      hideToast,
      copyToClipboard,
      handleImageError,
      formatDate,
      formatRelativeEnd,
      setStatusFilter,
      getTimeClass,
      getHistoryStatus,
      getOwnershipIcon,
      toggleViewMode,
      resetFilters,
      filterOffers,
      loadOffers,
      handleScroll,
      showDetails,
      closeModal,
      refreshOffer,
      refreshFromList,
      toggleOwnership,
      ownershipLoading,
      openPackage,
      openAcquisition,
      unfollowOffer,
    };
  },
};
</script>
<style scoped>
.offers-page {
  --navy: #152033;
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  max-width: var(--content-width);
  margin: 0 auto 28px;
}
/* Same heading as "Achats remportés" and "Collection" */
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action,
.secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid var(--navy);
  border-radius: 8px;
  background: var(--navy);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.secondary-action {
  border-color: #d5dce5;
  background: #fff;
  color: #455165;
}
.summary-strip {
  display: flex;
  align-items: stretch;
  max-width: var(--content-width);
  margin: 0 auto 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.summary-strip > div {
  display: flex;
  min-width: 180px;
  flex-direction: column;
  padding: 16px 20px;
  border-right: 1px solid var(--line);
}
.summary-strip strong {
  font-size: 20px;
  line-height: 1.1;
}
.summary-strip span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
}
.summary-strip .summary-strip__hint {
  min-width: 0;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  border: 0;
}
.live-dot {
  width: 7px;
  height: 7px;
  margin: 0 4px 0 0 !important;
  border-radius: 50%;
  background: #e89c3d;
}
.offers-panel {
  max-width: var(--content-width);
  margin: auto;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(20, 32, 51, 0.035);
}
.status-tabs {
  display: flex;
  gap: 24px;
  height: 53px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
}
.status-tabs button {
  position: relative;
  border: 0;
  background: none;
  color: #778294;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}
.status-tabs button.active {
  color: var(--text);
}
.status-tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--green);
  content: "";
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #fbfcfd;
}
.search-control {
  display: flex;
  align-items: center;
  gap: 9px;
  width: min(420px, 100%);
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #8893a3;
}
.search-control:focus-within {
  border-color: #67ad96;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.09);
}
.search-control input {
  width: 100%;
  border: 0;
  outline: 0;
  background: none;
  color: var(--text);
  font-size: 13px;
}
.source-filter {
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #4e5a6c;
  font-size: 13px;
}
.search-scope {
  margin: -6px 0 12px;
  color: #687487;
  font-size: 12px;
}
.reset-action {
  border: 0;
  background: none;
  color: #687487;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
.offers-content {
  min-height: 260px;
}
.table-scroll {
  overflow-x: auto;
}
.offers-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.offers-table th:first-child {
  width: auto;
}
.offers-table th:nth-child(2) {
  width: 105px;
}
.offers-table th:nth-child(3) {
  width: 175px;
}
.offers-table th:nth-child(4) {
  width: 130px;
}
.offers-table th:last-child {
  width: 205px;
}
.offers-table th {
  padding: 11px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff;
  color: #7d8796;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
.offers-table td {
  min-width: 0;
  padding: 13px 16px;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
}
.offers-table tbody tr {
  transition: background 0.15s;
}
.offers-table tbody tr:hover {
  background: #fafcfd;
}
.offer-identity {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}
.thumbnail-wrap {
  display: grid;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #e1e6ec;
  border-radius: 7px;
  background: #f1f4f7;
}
.thumbnail-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.identity-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
}
.identity-copy strong {
  width: 100%;
  overflow: hidden;
  color: #283446;
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The price guide may wrap in a narrow column */
.identity-copy small.cote-line {
  overflow: visible;
  white-space: normal;
}
.identity-copy small {
  overflow: hidden;
  color: #8993a1;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.compact-source {
  display: none;
}
.source-tag {
  display: inline-flex;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f1f4f7;
  color: #536073;
  font-size: 11px;
  font-weight: 700;
}
.deadline {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}
.deadline strong {
  font-size: 13px;
}
.deadline small {
  color: #8993a1;
  font-size: 11px;
}
.ending-soon .deadline strong {
  color: #c7433d;
}
.ending-today .deadline strong {
  color: #a66717;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}
.status-badge span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.status-badge.active {
  color: #177457;
  background: #eaf7f2;
}
.status-badge.owned {
  color: #2e6098;
  background: #edf4fb;
}
.status-badge.lost {
  color: #777f8b;
  background: #f0f2f4;
}
.row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.refresh-button,
.more-button {
  height: 34px;
  border: 1px solid #d8dee6;
  border-radius: 7px;
  background: #fff;
  color: #596679;
  cursor: pointer;
}
.refresh-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
}
.refresh-button .spinning {
  display: inline-block;
}
.refresh-button .spinning.spinning {
  animation: spin 0.7s linear infinite;
}
.more-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.refresh-button:disabled {
  opacity: 0.45;
  cursor: wait;
}
.empty-state {
  display: flex;
  min-height: 360px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 40px;
  text-align: center;
}
.empty-state__icon {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 12px;
  background: #eaf7f2;
  color: var(--green);
}
.empty-state h2 {
  margin: 16px 0 7px;
  font-size: 17px;
}
.empty-state p {
  max-width: 380px;
  margin: 0 0 18px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.55;
}
.loader-container {
  min-height: 360px;
}
.loading-more,
.end-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #86909d;
  font-size: 12px;
}
.spinner-circle {
  width: 14px;
  height: 14px;
  border: 2px solid #dbe2e9;
  border-top-color: var(--green);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.end-indicator {
  margin: 0;
  border-top: 1px solid #edf0f4;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.offers-toast {
  position: fixed;
  right: 24px;
  bottom: calc(24px + var(--safe-bottom));
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 18px;
  max-width: min(420px, calc(100vw - 32px));
  padding: 14px 16px;
  border: 1px solid #d8dee6;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 12px 35px rgba(20, 32, 51, 0.16);
  font-size: 13px;
}
.offers-toast__content {
  display: flex;
  align-items: center;
  gap: 9px;
}
.offers-toast__close {
  border: 0;
  background: none;
  color: #788394;
  cursor: pointer;
}
.offers-toast-enter-active,
.offers-toast-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}
.offers-toast-enter-from,
.offers-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 1400px) {
  .offers-table th:nth-child(2),
  .offers-table td:nth-child(2) {
    display: none;
  }
  .offers-table th:nth-child(3) {
    width: 155px;
  }
  .offers-table th:nth-child(4) {
    width: 118px;
  }
  .offers-table th:last-child {
    width: 150px;
  }
  .compact-source {
    display: inline;
  }
  .refresh-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
  .refresh-button {
    width: 34px;
    justify-content: center;
    padding: 0;
  }
  .more-button {
    padding: 0 9px;
  }
  .offers-table th,
  .offers-table td {
    padding-inline: 12px;
  }
}
@media (max-width: 800px) {
  .offers-page {
    padding: 20px 14px;
  }
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .summary-strip__hint {
    display: none !important;
  }
  .summary-strip > div {
    min-width: 0;
    flex: 1;
  }
  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .search-control {
    width: 100%;
  }
  .offers-content {
    max-height: none;
  }
  .status-tabs {
    gap: 16px;
    overflow-x: auto;
  }
  .page-heading h1 {
    font-size: var(--page-title-size);
  }
}
@media (max-width: 560px) {
  .offers-table th:nth-child(4),
  .offers-table td:nth-child(4) {
    display: none;
  }
  /* Phone: the announcement column keeps the room left by the others */
  .offers-table th:nth-child(3) {
    width: 96px;
  }
  .offers-table th:last-child {
    width: 54px;
  }
  .status-tabs button {
    white-space: nowrap;
  }
  .offers-table th,
  .offers-table td {
    padding: 11px 10px;
  }
  .offer-identity {
    min-width: 0;
  }
  .thumbnail-wrap {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }
  .more-button {
    display: none;
  }
}
/* "Remportée" quick action on ended offers: badge on the thumbnail */
.identity-cell {
  position: relative;
}
.won-badge {
  position: absolute;
  top: calc(50% + 8px);
  left: 34px;
  display: grid;
  width: 24px;
  height: 24px;
  padding: 0;
  place-items: center;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #2e6098;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
  box-shadow: 0 1px 4px rgb(16 24 40 / 25%);
}
.won-badge:hover {
  background: #168a64;
}
.won-badge:disabled {
  opacity: 0.6;
  cursor: wait;
}
@media (max-width: 800px) {
  .won-badge {
    top: calc(50% + 4px);
    left: 26px;
  }
}
</style>
